import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { fr, de, enUS } from 'date-fns/locale';
import { TrendingUp, Users, Clock, Calendar, ChevronLeft, ChevronRight, Thermometer } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Stats() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'de' ? de : language === 'en' ? enUS : fr;
  
  const [teamStats, setTeamStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('month'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState('all'); // Filter by user
  const [showWorked, setShowWorked] = useState(true);
  const [showLeave, setShowLeave] = useState(true);
  const [showSick, setShowSick] = useState(true);

  const getDateRange = useCallback(() => {
    if (viewMode === 'week') {
      return {
        start: format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        end: format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
      };
    } else {
      return {
        start: format(startOfMonth(currentDate), 'yyyy-MM-dd'),
        end: format(endOfMonth(currentDate), 'yyyy-MM-dd')
      };
    }
  }, [currentDate, viewMode]);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setTeam(res.data);
    } catch (err) {
      // Ignore
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      const [teamRes, advancedRes] = await Promise.all([
        axios.get(`${API}/stats/team`),
        axios.get(`${API}/stats/advanced`, {
          params: { start_date: range.start, end_date: range.end }
        })
      ]);
      setTeamStats(teamRes.data);
      setAdvancedStats(advancedRes.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [getDateRange, t]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const navigatePeriod = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : subMonths(currentDate, -1));
    }
  };

  const getPeriodLabel = () => {
    const range = getDateRange();
    if (viewMode === 'week') {
      return `${format(new Date(range.start), 'dd MMM', { locale })} - ${format(new Date(range.end), 'dd MMM yyyy', { locale })}`;
    }
    return format(currentDate, 'MMMM yyyy', { locale });
  };

  // Filter stats by selected user
  const filteredStats = selectedUser === 'all' 
    ? advancedStats 
    : advancedStats.filter(s => s.user_id === selectedUser);

  // Prepare chart data with stacked values
  const chartData = filteredStats.map(stat => {
    // Convert leave and sick days to hours (assuming 8h per day)
    const leaveHours = stat.leave_days * 8;
    const sickHours = stat.sick_days * 8;
    
    return {
      name: stat.user_name.split(' ')[0],
      fullName: stat.user_name,
      worked: showWorked ? stat.worked_hours : 0,
      leave: showLeave ? leaveHours : 0,
      sick: showSick ? sickHours : 0,
      total: (showWorked ? stat.worked_hours : 0) + (showLeave ? leaveHours : 0) + (showSick ? sickHours : 0),
      contractual: stat.contractual_hours,
      color: stat.color
    };
  });

  // Calculate totals
  const totals = filteredStats.reduce((acc, stat) => {
    const leaveHours = stat.leave_days * 8;
    const sickHours = stat.sick_days * 8;
    return {
      worked: acc.worked + stat.worked_hours,
      leave: acc.leave + leaveHours,
      sick: acc.sick + sickHours,
      contractual: acc.contractual + stat.contractual_hours,
      leaveDays: acc.leaveDays + stat.leave_days,
      sickDays: acc.sickDays + stat.sick_days
    };
  }, { worked: 0, leave: 0, sick: 0, contractual: 0, leaveDays: 0, sickDays: 0 });
  
  const totalActive = (showWorked ? totals.worked : 0) + (showLeave ? totals.leave : 0) + (showSick ? totals.sick : 0);
  const variance = totalActive - totals.contractual;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stats-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('statistics').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">{t('actualVsContractual')}</p>
        </div>
        
        <div className="flex items-center gap-4 flex-wrap">
          {/* User filter */}
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger className="w-48" data-testid="user-filter">
              <SelectValue placeholder={t('allCollaborators')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allCollaborators')}</SelectItem>
              {team.map(member => (
                <SelectItem key={member.id} value={member.id}>
                  {member.first_name} {member.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* View mode selector */}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">{t('week')}</SelectItem>
              <SelectItem value="month">{t('month')}</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigatePeriod('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[180px] text-center font-medium">{getPeriodLabel()}</span>
            <Button variant="outline" size="icon" onClick={() => navigatePeriod('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap border border-border rounded-lg p-4 bg-card">
        <span className="text-sm font-medium">{t('filters')}:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showWorked}
            onChange={(e) => setShowWorked(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <Clock className="h-4 w-4 text-primary" />
            {t('totalHours')}
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showLeave}
            onChange={(e) => setShowLeave(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <Calendar className="h-4 w-4 text-amber-500" />
            {t('paidLeave')}
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showSick}
            onChange={(e) => setShowSick(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm flex items-center gap-1">
            <Thermometer className="h-4 w-4 text-red-500" />
            {t('sickDays')}
          </span>
        </label>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">{t('totalHours')}</p>
              <p className="stat-value">{totals.worked.toFixed(1)}{t('h')}</p>
            </div>
            <div className="p-2 bg-primary/10 text-primary rounded-lg">
              <Clock className="h-5 w-5" />
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">{t('paidLeave')}</p>
              <p className="stat-value">{totals.leaveDays} {t('days')} ({totals.leave.toFixed(0)}{t('h')})</p>
            </div>
            <div className="p-2 bg-amber-500/10 text-amber-500 rounded-lg">
              <Calendar className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">{t('sickDays')}</p>
              <p className="stat-value">{totals.sickDays} {t('days')} ({totals.sick.toFixed(0)}{t('h')})</p>
            </div>
            <div className="p-2 bg-red-500/10 text-red-500 rounded-lg">
              <Thermometer className="h-5 w-5" />
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">{t('contractualHours')}</p>
              <p className="stat-value">{totals.contractual.toFixed(1)}{t('h')}</p>
            </div>
            <div className="p-2 bg-slate-500/10 text-slate-500 rounded-lg">
              <Users className="h-5 w-5" />
            </div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="stat-label">{t('variance')}</p>
              <p className={`stat-value ${variance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {variance >= 0 ? '+' : ''}{variance.toFixed(1)}{t('h')}
              </p>
              <p className="text-xs text-muted-foreground">
                ({totalActive.toFixed(1)}{t('h')} / {totals.contractual.toFixed(1)}{t('h')})
              </p>
            </div>
            <div className="p-2 bg-purple-500/10 text-purple-500 rounded-lg">
              <TrendingUp className="h-5 w-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Hours Chart - Stacked bar showing worked + leave + sick */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <h3 className="text-lg mb-4">{t('actualVsContractual').toUpperCase()}</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis 
                type="category" 
                dataKey="name" 
                width={80}
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 'var(--radius)'
                }}
                formatter={(value, name) => {
                  const labels = {
                    worked: t('totalHours'),
                    leave: t('paidLeave'),
                    sick: t('sickDays'),
                    contractual: t('contractualHours')
                  };
                  return [`${value.toFixed(1)}${t('h')}`, labels[name] || name];
                }}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
              />
              <Legend 
                formatter={(value) => {
                  const labels = {
                    worked: t('totalHours'),
                    leave: t('paidLeave'),
                    sick: t('sickDays'),
                    contractual: t('contractualHours')
                  };
                  return labels[value] || value;
                }}
              />
              {showWorked && <Bar dataKey="worked" stackId="a" fill="hsl(var(--primary))" name="worked" />}
              {showLeave && <Bar dataKey="leave" stackId="a" fill="#F59E0B" name="leave" />}
              {showSick && <Bar dataKey="sick" stackId="a" fill="#EF4444" name="sick" />}
              <Bar dataKey="contractual" fill="hsl(var(--muted-foreground))" name="contractual" opacity={0.3} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Stats Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('collaborator')}</th>
              <th className="text-right">{t('totalHours')}</th>
              <th className="text-right">{t('paidLeave')}</th>
              <th className="text-right">{t('sickDays')}</th>
              <th className="text-right">Total</th>
              <th className="text-right">{t('contractualHours')}</th>
              <th className="text-right">{t('variance')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredStats.map((stat) => {
              const leaveHours = stat.leave_days * 8;
              const sickHours = stat.sick_days * 8;
              const total = stat.worked_hours + leaveHours + sickHours;
              const statVariance = total - stat.contractual_hours;
              
              return (
                <tr key={stat.user_id}>
                  <td className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: stat.color }}
                    />
                    <span className="font-medium">{stat.user_name}</span>
                  </td>
                  <td className="text-right mono">{stat.worked_hours.toFixed(1)}{t('h')}</td>
                  <td className="text-right mono text-amber-600">{stat.leave_days}j ({leaveHours}{t('h')})</td>
                  <td className="text-right mono text-red-600">{stat.sick_days}j ({sickHours}{t('h')})</td>
                  <td className="text-right mono font-semibold">{total.toFixed(1)}{t('h')}</td>
                  <td className="text-right mono text-muted-foreground">{stat.contractual_hours.toFixed(1)}{t('h')}</td>
                  <td className="text-right">
                    <Badge className={`${statVariance >= 0 ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
                      {statVariance >= 0 ? '+' : ''}{statVariance.toFixed(1)}{t('h')}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
